import { message, Select, Table, TableColumnsType, TableProps, Typography } from "antd";
import { useEffect, useState, useRef } from "react";
import { Dropdown, OptionFilter, ReportFuelVolume } from "src/entity/fugitive/fugitive.entity";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { getOptions, getOptionsVocSeries, getReportFuelVolume, getYear } from "src/services/fugitive.service";
import { companyStore } from "src/stores/company-store";
import { profileStore } from "src/stores/profile-store";
import { useShallow } from "zustand/react/shallow";
import { canViewTable } from "../../PermissionCheck/PermissionCheck";
import { excelStore } from "src/stores/excel-store.interface";
import { fugitiveStore } from "src/stores/fugitive-store";
import { parseAndFormatScientificNumberWithoutZeroReturn, RoundDecimalToInteger } from "src/utils/process-number";
import { ArrayEquipmentVocSeries } from "src/entity/fugitive/fugitive.constants";
import { generateYearArray } from "src/utils/array";
import { formatTableText } from "src/components/AntdCustomPart/TableTitle.tsx/TableTitle";
import ReportVocSeriesUploadModal from "./ReportVocSeriesUploadModal";
import { registerOpenReportVocSeriesUpload } from "./reportVocSeriesUploadOpener";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];
const { Text } = Typography;

const ReportFugitiveFuelVolumeTable = () => {
  const profile = profileStore(useShallow((state) => state.profile));
  const loadingProfile = profileStore(useShallow((state) => state.loading));
  const { permissions } = useNavigationMenu();
  const [loading, setLoading] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [companyId, setCompanyId] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const prevYearRef = useRef<string | null>(null);
  const [selectedArea, setSelectedArea] = useState<string>("");
  const [selectedEmission, setSelectedEmission] = useState<string>("");
  const [optionYears, setOptionYears] = useState<number[]>([]);
  const [optionArea, setOptionArea] = useState<Dropdown[]>([]);
  const currentYear = new Date().getFullYear();
  const [messageApi, contextHolder] = message.useMessage();
  const [current, setCurrent] = useState(1);
  const [filter, setFilter] = useState<Filters>({});
  const [data, setData] = useState<ReportFuelVolume[]>([]);
  const [optionsFilter, setOptionFilter] = useState<OptionFilter[]>([]);
  const [optionFilterSeries, setOptionFilterSeries] = useState<Dropdown[]>([]);
  const [selectedSeries, setSelectedSeries] = useState<string>("all");
  const [areaFilter, setAreaFilter] = useState<[]>([]);
  const pageSize = 10;
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  useEffect(() => {
    return registerOpenReportVocSeriesUpload(() => setUploadModalOpen(true));
  }, []);

  const eqTypeFugitive = fugitiveStore(useShallow((state) => state.eqTypeFugitive));
  const getEqTypeFugitive = fugitiveStore(useShallow((state) => state.getEqTypeFugitive));

  const excelSetSelectedYear = excelStore(useShallow((state) => state.setSelectedYear));
  const excelSetQueryStr = excelStore(useShallow((state) => state.setQueryStr));

  const getAreaByCompanyId = companyStore(useShallow((state) => state.getAreaByCompanyId));
  const areaMaster = companyStore(useShallow((state) => state.dataArea));

  const optionComBySeriesFugitive = fugitiveStore(useShallow((state) => state.optionComBySeriesFugitive));
  const getOptionComBySeriesFugitive = fugitiveStore(useShallow((state) => state.getOptionComBySeriesFugitive));

  const fetchYear = () => {
    const years = generateYearArray();
    setOptionYears(years as number[]);
    setSelectedYear(years[0] as number);
  };

  const onChangeYear = (value: number) => {
    setSelectedYear(value);
  };

  const onChangeArea = (value: string) => {
    setSelectedArea(value);
    // setSelectedSeries("");
  };

  const onChangeEmission = (value: string) => {
    setSelectedEmission(value);
  };

  const onChangeSelectedSeries = (value: string) => {
    setSelectedSeries(value);
  };

  const onTableChange: TableProps<ReportFuelVolume>["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    setFilter(filters);
    setCurrent(current);
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = () => {
    let param: any = {};
    let offset = 0;
    let limit = pageSize;
    if (selectedYear) {
      param["year"] = selectedYear;
    }
    if (selectedSeries) {
      param["series"] = selectedSeries;
    }
    if (current) {
      let resultCalOffset = calPagination(current);
      if (resultCalOffset) {
        offset = resultCalOffset;
      }
      param["offset"] = offset;
    }
    if (limit) {
      param["limit"] = limit;
    }

    param["area"] = String(selectedArea);

    if (filter) {
      Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => {
          param[key] = `[${value?.join(",")}]`;
        })
        .join("");
    }
    return param;
  };

  const fetchReportVocSeries = async () => {
    try {
      if (selectedYear) {
        setLoadingData(true);
        let offset = 0;
        let resultCalOffset = calPagination(current);
        if (resultCalOffset) {
          offset = resultCalOffset;
        }
        const query = createParamFilter();
        const params = new URLSearchParams(query);
        const response = await getReportFuelVolume(params.toString());
        if (response.data.code === 200) {
          setLoading(false);
          const data = response.data?.data?.data || [];
          const total = response.data?.data?.total || 0;
          let tmpData =
            data?.map((el: any, index: number) => {
              return {
                no: (index + offset + 1).toString(),
                ...el,
              };
            }) || [];

          setData([...tmpData]);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  };

  const fetchOption = async () => {
    try {
      setLoadingFilter(true);
      if (selectedYear) {
        const response = await getOptions(selectedYear);
        if (response.data.code === 200) {
          const data = response.data?.data?.filter;

          console.log("data", data);
          if (data) {
            const areaFilter = data?.find((item: any) => item.key === "area")?.filter || [];
            setAreaFilter(areaFilter);
            setOptionFilter(data);
          } else {
            setAreaFilter([]);
            setOptionFilter([]);
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingFilter(false);
    }
  };

  const fetchOptionVocSeries = async (preserveSeriesSelection?: boolean) => {
    try {
      setLoadingFilter(true);
      if (selectedYear) {
        const filterParams = `area=${selectedArea}`;
        const queryString = new URLSearchParams(filterParams).toString();
        const response = await getOptionsVocSeries(selectedYear, queryString);
        if (response.data.code === 200) {
          const data = response.data?.data?.filter;

          if (data) {
            const formattedOptions = data
              .find((item: any) => item.key === "seriesName")
              ?.filter.map((value: any) => {
                return { label: value, value };
              });
            const options = [{ label: "All", value: "all" }, ...formattedOptions];
            setOptionFilterSeries(options);
            if (!preserveSeriesSelection) {
              setSelectedSeries(options[0].value);
            } else {
              const stillValid = options.some((o) => o.value === selectedSeries);
              if (!stillValid) {
                setSelectedSeries(options[0]?.value ?? "all");
              }
            }
          } else {
            setOptionFilterSeries([]);
            if (!preserveSeriesSelection) {
              setSelectedSeries("");
            }
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingFilter(false);
    }
  };

  const resetFilter = () => {
    setFilter({});
  };

  useEffect(() => {
    fetchYear();
    getEqTypeFugitive("");
  }, []);

  useEffect(() => {
    const queryParam = createParamFilter();
    if (queryParam) {
      const params = new URLSearchParams(queryParam);
      excelSetQueryStr(String(params));
    }
    if (selectedYear && Number(prevYearRef?.current) !== selectedYear) {
      fetchOption();
      fetchOptionVocSeries();
      excelSetSelectedYear(selectedYear);
      prevYearRef.current = selectedYear.toString();
    }
  }, [filter, selectedYear, selectedEmission, selectedArea, selectedSeries]);

  useEffect(() => {
    if (profile?.activeCompany) {
      const fetchData = async () => {
        const companyId = profile.activeCompany._id;
        if (companyId) {
          setSelectedYear(currentYear);
          resetFilter();
          await Promise.all([getAreaByCompanyId(companyId)]);
          fetchOption();
          fetchOptionVocSeries();
          setCompanyId(companyId);
        }
      };
      fetchData();
    }
  }, [profile?.activeCompany]);

  useEffect(() => {
    const defaultAll = {
      key: "default",
      value: "all",
      label: "All",
    };
    const tmpAreaFilter = areaFilter?.map((itemAreaName: string, index: number) => {
      const result = areaMaster.find((master) => master.areaName.toLowerCase() === String(itemAreaName).toLowerCase());
      return {
        key: String(index),
        value: result?._id || itemAreaName,
        label: itemAreaName,
      };
    });
    const optArea = [defaultAll];
    if (tmpAreaFilter) {
      optArea.push(...tmpAreaFilter);
    }
    setSelectedArea(optArea[0].value);
    setOptionArea(optArea);
  }, [areaFilter, areaMaster]);

  useEffect(() => {
    const fetchData = async () => {
      const isValidArea = selectedArea && selectedArea !== "0";
      const isZeroAreaWithSeries = String(selectedArea) === "0" && selectedSeries;
      if ((isValidArea && selectedSeries) || isZeroAreaWithSeries) {
        await Promise.all([fetchReportVocSeries()]);
      } else {
        setData([]);
      }
    };
    fetchData();
  }, [selectedYear, selectedArea, filter, selectedSeries]);

  useEffect(() => {
    if (selectedArea) {
      const query = selectedArea !== "all" ? `area=${selectedArea}` : "";
      // getOptionComBySeriesFugitive(query);
      fetchOptionVocSeries();
    }
  }, [selectedArea]);

  // useEffect(() => {
  //   const formattedOptions = optionComBySeriesFugitive.map((item) => ({
  //     label: item,
  //     value: item,
  //   }));

  //   const options = [{ label: "All", value: "all" }, ...formattedOptions];
  //   setOptionFilterSeries(options);
  //   setSelectedSeries(options[0].value);
  // }, [optionComBySeriesFugitive]);

  const getRowSpan = (index: number, record: any, dataTable: any[]) => {
    if (index === 0 || dataTable[index - 1].equipmentType !== record.equipmentType) {
      return dataTable.filter((item) => item.equipmentType === record.equipmentType).length;
    }
    return 0;
  };

  const columns: TableColumnsType = [
    {
      title: formatTableText("Equipment Type", ["-"], 9),
      align: "center",
      dataIndex: "equipmentType",
      filteredValue: filter?.equipmentType || null,
      filters: ArrayEquipmentVocSeries.map((item, index) => {
        return {
          key: item + index,
          value: item,
          text: item,
        };
      }),
      render: (text, record, index) => {
        const rowSpan = getRowSpan(index, record, data);
        return {
          children: <Text>{text}</Text>,
          props: {
            rowSpan,
          },
        };
      },
    },
    {
      title: "Service",
      dataIndex: "service",
      align: "center",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Equipment (point)",
      align: "center",
      children: [
        {
          title: "Total",
          dataIndex: "totalEquipmentPoint",
          align: "center",
          render: (text) => <Text>{RoundDecimalToInteger(text)}</Text>,
        },
        {
          title: "Measured",
          dataIndex: "measuredEquipment",
          align: "center",
          render: (text) => <Text>{RoundDecimalToInteger(text)}</Text>,
        },
        {
          title: "Exempt",
          dataIndex: "exemptEquipment",
          align: "center",
          render: (text) => <Text>{RoundDecimalToInteger(text)}</Text>,
        },
        {
          title: "Exceeding",
          dataIndex: "exceedingEquipment",
          align: "center",
          render: (text) => <Text>{RoundDecimalToInteger(text)}</Text>,
        },
        {
          title: "Repaired",
          dataIndex: "repairedEquipment",
          align: "center",
          render: (text) => <Text>{RoundDecimalToInteger(text)}</Text>,
        },
      ],
    },
    {
      title: "Emission (Kg/y)",
      children: [
        {
          title: "TVOCs",
          dataIndex: "tvocs",
          align: "center",
          render: (text) => <Text>{parseAndFormatScientificNumberWithoutZeroReturn(text, 2)}</Text>,
        },
        {
          title: "BZ",
          dataIndex: "bz",
          align: "center",
          render: (text) => <Text>{parseAndFormatScientificNumberWithoutZeroReturn(text, 2)}</Text>,
        },
        {
          title: "BD",
          dataIndex: "bd",
          align: "center",
          render: (text) => <Text>{parseAndFormatScientificNumberWithoutZeroReturn(text, 2)}</Text>,
        },
        {
          title: "VCM",
          dataIndex: "vcm",
          align: "center",
          render: (text) => <Text>{parseAndFormatScientificNumberWithoutZeroReturn(text, 2)}</Text>,
        },
        {
          title: "EDC",
          dataIndex: "edc",
          align: "center",
          render: (text) => <Text>{parseAndFormatScientificNumberWithoutZeroReturn(text, 2)}</Text>,
        },
      ],
    },
  ];

  return (
    <div>
      {contextHolder}
      <div style={{ display: "flex", paddingBottom: 10 }}>
        <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
          <Select
            loading={loading || loadingProfile || loadingFilter}
            value={selectedYear}
            style={{ width: 120 }}
            onChange={onChangeYear}
            options={optionYears.map((year) => ({ label: year, value: year }))}
            size="middle"
          />
          <Select
            value={selectedArea}
            style={{ width: 120 }}
            onChange={onChangeArea}
            options={optionArea}
            loading={loadingFilter || loadingProfile}
            size="middle"
          />
          <Select
            value={selectedSeries}
            style={{ width: 120 }}
            onChange={onChangeSelectedSeries}
            options={optionFilterSeries}
            loading={loading || loadingProfile}
            size="middle"
          />
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={canViewTable(permissions) ? data : []}
        scroll={{ x: "max-content" }}
        rowKey={(record, index) => `${record._id}-${index}`}
        pagination={false}
        onChange={onTableChange}
        loading={loading || loadingData || loadingProfile || loadingFilter}
      />
      <ReportVocSeriesUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={async () => {
          await fetchOptionVocSeries(true);
          await fetchReportVocSeries();
        }}
      />
    </div>
  );
};

export default ReportFugitiveFuelVolumeTable;
